/**
 * The DESKTOP uploads of one mealtime step (PROD-1511): one strip, about the
 * height of the single scan card it replaces and in the same place, above the
 * table, with a drop target per paper side by side and Read at the end.
 *
 * Each target keeps only what tells the papers apart: a small sheet icon, the
 * paper's name and photo count, one line on which paper it is, its photos and
 * an add control (or Upload while empty). Everything else, the drawing and
 * what a photo fills, opens from the sheet icon in a popover, so the table
 * still starts right under the strip. Typing stays in the table: desktop asks
 * no scan-or-type question.
 *
 * Owns its queue and its read. `onExtract` runs the flow's two-paper scan for
 * the open step; the strip only says what came back (filled X of Y, what to
 * check, what could not be read) and empties its queue once the numbers are in
 * the table. A file dropped on a target joins that target's queue.
 */

import { Button } from '@ordo/ui/button'
import { cn } from '@ordo/ui/cn'
import { Popover, PopoverContent, PopoverTrigger } from '@ordo/ui/popover'
import { AlertTriangle, CheckCircle2, Loader2, Plus, Upload, X } from 'lucide-react'
import { type DragEvent, useRef, useState } from 'react'

import { SheetPreview } from '@/components/production/sheets/sheet-preview'
import {
  photoCountLabel,
  SHEET_PAPER_DESCRIPTION,
  usePhotoPreviews,
} from '@/components/production/sheets/sheet-upload-card'
import { type ScanApplySummary, SCHOOL_SHEET_SCAN_COPY } from '@/components/tasks/sheet-scan-card'
import { mergePhotos, photoKey } from '@/lib/production-records/mobile-wizard'
import {
  EMPTY_SHEET_PHOTOS,
  readPhotosLabel,
  SHEET_PAPER_NAME,
  type SheetPaper,
  sheetPhotoCount,
  type SheetPhotos,
} from '@/lib/production-records/sheet-papers'
import { ACCEPTED_IMAGE_TYPES } from '@/lib/sheet-scan/images'

/** The old desktop card's ceiling for one mealtime: a page or two of each paper. */
export const DESKTOP_SHEET_PHOTO_CAP = 6

/** The types the reader takes; the desktop picker shows nothing else. */
const DESKTOP_ACCEPT = ACCEPTED_IMAGE_TYPES.join(',')

export interface SheetUploadStripProps {
  /** Run the flow's scan for the open step: both papers' photos at once. */
  onExtract: (photos: SheetPhotos) => Promise<ScanApplySummary>
  /** Offer the Orders sheet: false on a step with no truck to receive. */
  showDelivery: boolean
  /** Offer the production record: false on a step with no record. */
  showRecord: boolean
  /** One line under "Delivery sheet" (`deliveryStripLine`). */
  deliveryLine: string
  /** One line under "Production record" (`recordStripLine`). */
  recordLine: string
  disabled?: boolean
  className?: string
}

type Phase = 'idle' | 'reading' | 'done' | 'error'

export function SheetUploadStrip({
  onExtract,
  showDelivery,
  showRecord,
  deliveryLine,
  recordLine,
  disabled = false,
  className,
}: SheetUploadStripProps) {
  const [photos, setPhotos] = useState<SheetPhotos>(EMPTY_SHEET_PHOTOS)
  const [phase, setPhase] = useState<Phase>('idle')
  const [summary, setSummary] = useState<ScanApplySummary | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [capMessage, setCapMessage] = useState<string | null>(null)
  const count = sheetPhotoCount(photos)
  const reading = phase === 'reading'

  function add(paper: SheetPaper, incoming: File[]): void {
    const others = paper === 'delivery' ? photos.record.length : photos.delivery.length
    const merged = mergePhotos(photos[paper], incoming, Math.max(DESKTOP_SHEET_PHOTO_CAP - others, 0))
    setPhotos((prev) => ({ ...prev, [paper]: merged.photos }))
    setCapMessage(merged.capMessage ? `You can send up to ${DESKTOP_SHEET_PHOTO_CAP} photos for one mealtime.` : null)
  }

  function remove(paper: SheetPaper, key: string): void {
    setPhotos((prev) => ({ ...prev, [paper]: prev[paper].filter((file) => photoKey(file) !== key) }))
    setCapMessage(null)
  }

  async function read(): Promise<void> {
    if (count === 0 || reading) {
      return
    }
    setPhase('reading')
    setError(null)
    try {
      const result = await onExtract(photos)
      setSummary(result)
      // The numbers are in the table now; the photos are not kept (D22).
      setPhotos(EMPTY_SHEET_PHOTOS)
      setCapMessage(null)
      setPhase('done')
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : SCHOOL_SHEET_SCAN_COPY.errorFallback)
      setPhase('error')
    }
  }

  const papers: SheetPaper[] = [
    ...(showDelivery ? (['delivery'] as const) : []),
    ...(showRecord ? (['record'] as const) : []),
  ]

  return (
    <section
      aria-label="Add photos of your sheets"
      data-sheet-upload-strip
      className={cn('rounded-[10px] border border-dashed bg-muted/30 px-4 py-3', className)}>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
        {papers.map((paper, i) => (
          <div key={paper} className="contents">
            {i > 0 ? <span aria-hidden="true" className="hidden h-12 w-px bg-border xl:block" /> : null}
            <SheetTarget
              paper={paper}
              line={paper === 'delivery' ? deliveryLine : recordLine}
              photos={photos[paper]}
              onAdd={(files) => add(paper, files)}
              onRemove={(key) => remove(paper, key)}
              full={count >= DESKTOP_SHEET_PHOTO_CAP}
              disabled={disabled || reading}
            />
          </div>
        ))}
        <Button
          type="button"
          size="sm"
          className="shrink-0 rounded-full"
          disabled={disabled || reading || count === 0}
          onClick={() => void read()}>
          {reading ? (
            <>
              <Loader2 className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
              Reading…
            </>
          ) : (
            readPhotosLabel(count)
          )}
        </Button>
      </div>
      {capMessage ? <p className="mt-2 text-xs text-amber-700 dark:text-amber-500">{capMessage}</p> : null}
      {phase === 'error' && error ? (
        <p role="alert" className="mt-2.5 flex items-start gap-2 text-[13px] leading-[18px] text-destructive">
          <AlertTriangle className="mt-px size-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      ) : null}
      {phase === 'done' && summary ? <StripSummary summary={summary} /> : null}
    </section>
  )
}

/** What the read filled and what to check, under the strip. */
function StripSummary({ summary }: { summary: ScanApplySummary }) {
  const checks = [
    ...(summary.lowConfidenceCount > 0
      ? [
          summary.lowConfidenceCount === 1
            ? '1 value needs a second look.'
            : `${summary.lowConfidenceCount} values need a second look.`,
        ]
      : []),
    ...summary.warnings,
    ...(summary.unmatchedRowsSeen.length > 0
      ? [`We saw rows on the sheets we could not place: ${summary.unmatchedRowsSeen.join(', ')}.`]
      : []),
  ]
  return (
    <div role="status" className="mt-2.5 space-y-0.5 text-[13px] leading-[18px]">
      <p className="flex items-center gap-2">
        <CheckCircle2 className="size-4 shrink-0 text-emerald-600" aria-hidden="true" />
        Filled {summary.filledCount} of {summary.totalCount} {SCHOOL_SHEET_SCAN_COPY.filledUnit}. Double-check them,
        then confirm.
      </p>
      {checks.map((line) => (
        <p key={line} className="pl-6 text-amber-700 dark:text-amber-500">
          {line}
        </p>
      ))}
    </div>
  )
}

/** A paper's small icon: its rows, and the columns a photo of it fills picked out. */
function SheetGlyph({ paper }: { paper: SheetPaper }) {
  const filled = paper === 'record' ? 5 : 2
  return (
    <span aria-hidden="true" className="flex h-full w-full flex-col gap-[3px]">
      {paper === 'record' ? (
        <span className="h-[5px] w-[18px] rounded-[1px] border border-foreground" />
      ) : (
        <span className="h-[2px] w-3/5 rounded-full bg-muted-foreground/60" />
      )}
      {[0, 1, 2].map((line) => (
        <span key={line} className="flex gap-px">
          <span className="mt-px h-[2px] flex-1 rounded-full bg-muted-foreground/30" />
          {Array.from({ length: filled }, (_v, i) => (
            <span key={i} className={cn('h-1 rounded-[1px] bg-foreground/35', filled === 5 ? 'w-[3px]' : 'w-1.5')} />
          ))}
        </span>
      ))}
      <span className="h-1.5 rounded-[1px] bg-foreground/35" />
    </span>
  )
}

interface SheetTargetProps {
  paper: SheetPaper
  line: string
  photos: readonly File[]
  onAdd: (files: File[]) => void
  onRemove: (key: string) => void
  full: boolean
  disabled: boolean
}

/** One paper's drop target: icon (with its popover), name, one line, photos, add. */
function SheetTarget({ paper, line, photos, onAdd, onRemove, full, disabled }: SheetTargetProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const previews = usePhotoPreviews(photos)
  const [dragging, setDragging] = useState(false)
  const name = SHEET_PAPER_NAME[paper]

  function onDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault()
    setDragging(false)
    if (disabled || full) {
      return
    }
    onAdd(Array.from(event.dataTransfer.files).filter((file) => file.type.startsWith('image/')))
  }

  return (
    <div
      data-sheet-target={paper}
      className={cn(
        'flex min-w-0 flex-[1_1_380px] items-center gap-3 rounded-lg',
        dragging && 'ring-2 ring-primary/50 ring-offset-4 ring-offset-background',
      )}
      onDragOver={(event) => {
        event.preventDefault()
        setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`See the ${name.toLowerCase()} and what it fills`}
            className="h-12 w-10 shrink-0 rounded border bg-background p-[5px] transition-colors hover:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <SheetGlyph paper={paper} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[360px] p-4">
          <p className="text-sm font-semibold leading-5">{name}</p>
          <p className="mt-1 text-[13px] leading-[19px] text-muted-foreground">{SHEET_PAPER_DESCRIPTION[paper]}</p>
          <SheetPreview paper={paper} className="mt-3" />
        </PopoverContent>
      </Popover>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-sm font-medium leading-5">{name}</span>
          {photos.length > 0 ? (
            <span className="text-xs text-muted-foreground">{photoCountLabel(photos.length)}</span>
          ) : null}
        </div>
        <p className="mt-0.5 text-xs leading-4 text-muted-foreground">{line}</p>
      </div>
      {photos.length > 0 ? (
        <div className="flex shrink-0 items-center gap-2">
          {previews.map((preview, i) => (
            <span key={preview.key} className="group relative">
              <img
                src={preview.url}
                alt={`${name} photo ${i + 1}`}
                className="size-10 rounded-md border object-cover"
              />
              <button
                type="button"
                aria-label={`Remove ${name.toLowerCase()} photo ${i + 1}`}
                disabled={disabled}
                className="absolute -right-1.5 -top-1.5 flex size-5 items-center justify-center rounded-full border bg-background text-muted-foreground"
                onClick={() => onRemove(preview.key)}>
                <X className="size-3" aria-hidden="true" />
              </button>
            </span>
          ))}
          <button
            type="button"
            aria-label={`Add ${name.toLowerCase()} photos`}
            disabled={disabled || full}
            className="flex size-10 items-center justify-center rounded-md border-[1.5px] border-dashed border-muted-foreground/50 bg-background text-foreground/80 disabled:opacity-50"
            onClick={() => inputRef.current?.click()}>
            <Plus className="size-[18px]" aria-hidden="true" />
          </button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0 rounded-full"
          disabled={disabled || full}
          aria-label={`Upload ${name.toLowerCase()} photos`}
          onClick={() => inputRef.current?.click()}>
          <Upload className="mr-1.5 size-4" aria-hidden="true" />
          Upload
        </Button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={DESKTOP_ACCEPT}
        multiple
        className="hidden"
        data-testid={`sheet-strip-input-${paper}`}
        onChange={(event) => {
          onAdd(Array.from(event.target.files ?? []))
          event.target.value = ''
        }}
      />
    </div>
  )
}
